import { productImageAlt } from "@/lib/imageAlt";
import { useState } from "react";
import { cleanProductTitle } from "@/lib/productTitle";
import { ShopifyProduct, formatPrice, getShopifyImageUrl } from "@/lib/shopify";
import { getDiscountPercent, genuineOriginalPrice } from "@/lib/pricing";
import { defaultVariant, listingPrice } from "@/lib/variants";
import { useCartStore } from "@/stores/cartStore";
import { useWishlistStore } from "@/stores/wishlistStore";
import { useCompareStore } from "@/stores/compareStore";
import { Heart, Eye, ArrowLeftRight, ShoppingCart, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { QuickViewModal } from "./QuickViewModal";
import { useProductRating } from "@/hooks/useProductRating";
import { StarRating } from "./StarRating";
import { Button } from "./ui/button";

interface ProductCardProps {
  product: ShopifyProduct;
  /** Pre-computed, guaranteed-unique display name (see uniqueDisplayNames). */
  displayName?: string;
  /**
   * Show the price the product page opens on when the title names a dearer
   * in-stock variant than the cheapest one. For rails with no price sort or
   * filter (guide picks): "8' x 10' … Rug — From $141.99" was the 2' x 3'.
   */
  openingPrice?: boolean;
}


const colorMap: Record<string, string> = {
  black: "#1a1a1a", white: "#ffffff", red: "#c0392b", blue: "#2980b9",
  green: "#27ae60", yellow: "#f1c40f", orange: "#e67e22", pink: "#e91e63",
  purple: "#8e44ad", gray: "#95a5a6", grey: "#95a5a6", brown: "#795548",
  navy: "#1a237e", beige: "#d4c5a9", cream: "#fffdd0", ivory: "#fffff0",
  tan: "#d2b48c", walnut: "#5c4033", oak: "#c19a6b", natural: "#c8b88a",
  charcoal: "#36454f", slate: "#708090", sand: "#c2b280", copper: "#b87333",
  gold: "#d4af37", silver: "#c0c0c0", bronze: "#cd7f32", teal: "#008080",
  olive: "#808000", burgundy: "#800020", coral: "#ff7f50", mint: "#98ff98",
  sage: "#9caf88", rust: "#b7410e", taupe: "#483c32", espresso: "#3c1414",
  midnight: "#191970", stone: "#928e85", pewter: "#8e8e8e",
};

function getSwatchColor(value: string): string | null {
  const lower = value.toLowerCase().trim();
  if (colorMap[lower]) return colorMap[lower];
  for (const [key, hex] of Object.entries(colorMap)) {
    if (lower.includes(key)) return hex;
  }
  return null;
}

// The second photo only ever shows on hover. Phones cannot hover, yet they
// downloaded it for every card: 34–41% of a category page's image bytes.
const CAN_HOVER =
  typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

// Candidates sized to the card's photo, not the page. In the two-column phone
// grid a square photo shows about 30vw wide (118 px on a 390 px screen), where
// the old smallest candidate, 600w, was nearly twice the pixels a 3x phone
// needs. From 768 px up the card is at most 28vw, then 367 px, wide.
const CARD_IMAGE_WIDTHS = [300, 400, 600, 800];
const CARD_IMAGE_SIZES = "(max-width: 767px) 30vw, (max-width: 1439px) 28vw, 367px";
const cardSrcSet = (url: string) =>
  CARD_IMAGE_WIDTHS.map((w) => `${getShopifyImageUrl(url, w)} ${w}w`).join(", ");

// Keep the full cleaned name: colour, material, piece count and configuration are
// what distinguish these SKUs, so we never truncate them away here.
function shortenTitle(title: string, productType?: string): string {
  return cleanProductTitle(title, productType);
}

export const ProductCard = ({ product, displayName, openingPrice }: ProductCardProps) => {
  const addItem = useCartStore((state) => state.addItem);
  const isLoading = useCartStore((state) => state.isLoading);
  const toggleWishlist = useWishlistStore((state) => state.toggle);
  const isWishlisted = useWishlistStore((state) => state.has(product.node.handle));
  const addToCompare = useCompareStore((state) => state.add);
  const isCompared = useCompareStore((state) => state.has(product.node.id));
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const { node } = product;
  const displayTitle = displayName ?? shortenTitle(node.title, node.productType);
  const numericId = node.id.replace(/^gid:\/\/shopify\/Product\//, "");
  const { rating, count: reviewCount } = useProductRating(numericId, node.handle, { fetch: false });

  const mainImage = node.images?.edges?.[0]?.node;
  const secondImage = CAN_HOVER ? node.images?.edges?.[1]?.node : undefined;
  const firstVariant = node.variants?.edges?.[0]?.node;
  // Lowest in-stock variant price; "From" when other in-stock variants cost
  // more, so an "8' x 10' … Rug" is not shown at its 2' x 3' price unqualified.
  const listed = listingPrice(node);
  const opening = openingPrice ? defaultVariant(node) : undefined;
  const titled =
    !!opening?.availableForSale && !!listed.price && parseFloat(opening.price.amount) > parseFloat(listed.price.amount);
  const price = titled ? opening!.price : listed.price;
  const compareAtPrice = titled ? opening!.compareAtPrice : listed.compareAtPrice;
  const priceIsFrom = titled ? false : listed.from;

  // A strikethrough is only ever rendered from Shopify's real compareAtPrice,
  // on something in stock, within the brand's sale cap.
  const currentPriceNum = parseFloat(price?.amount || "0");
  const displayOriginalPrice = genuineOriginalPrice(currentPriceNum, compareAtPrice?.amount, node.availableForSale !== false);
  const isOnSale = !!displayOriginalPrice;
  const discountPct = displayOriginalPrice ? getDiscountPercent(displayOriginalPrice, currentPriceNum) : 0;

  // Stock signal. Shopify's product-level flag is authoritative; when it is
  // absent we fall back to "no variant is buyable". We never show a quantity —
  // the Storefront token cannot read inventory, so any count would be invented.
  const variantEdges = node.variants?.edges || [];
  // "View options" only where there is a choice to make. A single variant with
  // a named option (Color: Black) has nothing to pick, so it keeps one-tap add.
  const requiresSelection =
    variantEdges.length > 1 ||
    (node.options ?? []).some((option) => (option.values?.length ?? 0) > 1);
  const soldOut =
    node.availableForSale === false ||
    (node.availableForSale === undefined &&
      variantEdges.length > 0 &&
      variantEdges.every((v) => v.node.availableForSale === false));
  // A card offers a control whenever something can be bought: "View options"
  // when any variant is in stock (the first one is often sold out), Quick Add
  // only for a lone in-stock variant, which is what it adds.
  const canOffer =
    !soldOut && (requiresSelection ? variantEdges.some((v) => v.node.availableForSale) : !!firstVariant?.availableForSale);

  const colorOption = node.options?.find(
    (o) => o.name.toLowerCase() === "color" || o.name.toLowerCase() === "colour"
  );
  const swatches =
    colorOption?.values
      ?.map((v) => ({ label: v, color: getSwatchColor(v) }))
      .filter((s) => s.color !== null)
      .slice(0, 5) || [];

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(node.handle);
    toast.success(isWishlisted ? "Removed from wishlist" : "Added to wishlist", {
      description: node.title,
      position: "top-center",
    });
  };

  const handleQuickView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickViewOpen(true);
  };

  const handleCompare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCompare(product);
    toast.success("Added to compare", { description: node.title, position: "top-center" });
  };

  const handleQuickAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!firstVariant || addingToCart) return;
    setAddingToCart(true);
    try {
      const result = await addItem({
        product,
        variantId: firstVariant.id,
        variantTitle: firstVariant.title,
        price: firstVariant.price,
        quantity: 1,
        selectedOptions: firstVariant.selectedOptions || [],
      });
      // When Shopify didn't add it (sold out), the cart store has said so itself.
      if (result.ok && !result.notice) toast.success("Added to cart", { description: displayTitle, position: "top-center" });
    } catch {
      toast.error("Failed to add to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  return (
    <>
      <div className="group">
        <Link to={`/product/${node.handle}`} className="block">
          {/* Image container — landscape box: these are wide products, portrait wastes the frame */}
          <div className="aspect-[4/3] overflow-hidden relative bg-muted/20">
            {soldOut && (
              <span className="absolute top-3 left-3 z-10 rounded-full bg-background text-foreground text-[10px] font-medium uppercase tracking-[0.14em] px-2.5 py-1">
                Sold out
              </span>
            )}
            {!soldOut && isOnSale && discountPct > 0 && (
              <span className="absolute top-3 left-3 z-10 rounded-full bg-primary text-primary-foreground text-[10px] font-medium uppercase tracking-[0.14em] px-2.5 py-1 animate-in fade-in duration-300">
                {discountPct}% Off
              </span>
            )}
            {/* Action buttons — always visible on touch, hover on desktop */}
            <div className="absolute top-2 right-2 z-10 flex flex-col gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-200 md:translate-x-2 md:group-hover:translate-x-0">
              <button
                onClick={handleToggleWishlist}
                aria-label={`${isWishlisted ? "Remove" : "Add"} ${displayTitle} ${isWishlisted ? "from" : "to"} wishlist`}
                className="h-9 w-9 md:h-7 md:w-7 flex items-center justify-center rounded-full bg-background shadow-sm transition-transform duration-150 hover:scale-110 active:scale-95"
              >
                <Heart
                  className={`h-4 w-4 md:h-3.5 md:w-3.5 transition-all duration-200 ${
                    isWishlisted ? "fill-destructive text-destructive scale-110" : "text-muted-foreground hover:text-foreground"
                  }`}
                />
              </button>
              <button
                onClick={handleCompare}
                aria-label={`Add ${displayTitle} to compare`}
                className={`h-9 w-9 md:h-7 md:w-7 hidden md:flex items-center justify-center rounded-full bg-background shadow-sm transition-transform duration-150 hover:scale-110 active:scale-95 ${isCompared ? "text-primary" : ""}`}
              >
                <ArrowLeftRight className="h-4 w-4 md:h-3.5 md:w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
              </button>
              <button
                onClick={handleQuickView}
                aria-label={`Quick view: ${displayTitle}`}
                className="h-9 w-9 md:h-7 md:w-7 hidden md:flex items-center justify-center rounded-full bg-background shadow-sm transition-transform duration-150 hover:scale-110 active:scale-95"
              >
                <Eye className="h-4 w-4 md:h-3.5 md:w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
              </button>
            </div>

            {/* Quick Add Button — bottom of card, show on hover. Only where the
                device can hover: on touch screens (tablets included) it was
                invisible but tappable, so a tap on the photo added the item to
                the cart instead of opening it. Those get the button under the card. */}
            {CAN_HOVER && canOffer && (
              requiresSelection ? (
                <span className="absolute bottom-3 left-3 right-3 z-10 hidden items-center justify-center gap-2 rounded-full bg-foreground py-2.5 text-[11px] font-semibold uppercase tracking-wider text-background opacity-0 transition-all duration-200 md:flex md:translate-y-2 md:group-hover:translate-y-0 md:group-hover:opacity-100">
                  View options
                </span>
              ) : (
                <Button
                  onClick={handleQuickAdd}
                  disabled={addingToCart}
                  className="absolute bottom-3 left-3 right-3 z-10 hidden h-auto rounded-full bg-foreground py-2.5 text-[11px] font-semibold uppercase tracking-wider text-background opacity-0 transition-all duration-200 hover:bg-foreground/90 md:flex md:translate-y-2 md:group-hover:translate-y-0 md:group-hover:opacity-100"
                >
                  {addingToCart ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <>
                      <ShoppingCart className="h-3.5 w-3.5" />
                      Quick Add
                    </>
                  )}
                </Button>
              )
            )}
            {mainImage ? (
              <>
                <img
                  src={getShopifyImageUrl(mainImage.url, 600)}
                  srcSet={cardSrcSet(mainImage.url)}
                  sizes={CARD_IMAGE_SIZES}
                  alt={productImageAlt(node.title, { displayName: displayTitle })}
                  loading="lazy"
                  decoding="async"
                  className={`w-full h-full object-contain transition-all duration-500 ease-out ${
                    secondImage ? "group-hover:opacity-0" : "group-hover:scale-[1.03]"
                  }`}
                />
                {secondImage && (
                  <img
                    src={getShopifyImageUrl(secondImage.url, 600)}
                    srcSet={cardSrcSet(secondImage.url)}
                    sizes={CARD_IMAGE_SIZES}
                    alt={productImageAlt(node.title, { displayName: displayTitle, index: 1, total: 2 })}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-contain opacity-0 scale-[1.02] group-hover:opacity-100 group-hover:scale-100 transition-all duration-700 ease-out"
                  />
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                No Image
              </div>
            )}
          </div>
        </Link>

        {/* Product info — centered like Article.com */}
        <div className="text-center pt-3 pb-1">
          <Link to={`/product/${node.handle}`} title={displayTitle}>
            <h3 className="text-[13.5px] font-normal text-foreground line-clamp-2 hover:text-primary transition-colors leading-snug">
              {displayTitle}
            </h3>
          </Link>

          {/* Differential spec badges — only when real data exists (no fabrication) */}
          {(() => {
            const badges: string[] = [];
            const tags = "tags" in node && Array.isArray(node.tags)
              ? node.tags.filter((tag): tag is string => typeof tag === "string")
              : undefined;
            const title = node.title.toLowerCase();
            // Only where the supplier's title says so (every sectional used to get it),
            // and only when the card name has dropped the word.
            if (/\bmodular\b/.test(title) && !/\bmodular\b/i.test(displayTitle)) badges.push("Modular");
            if (title.includes("sleeper") || tags?.some(t => /sleeper/i.test(t))) badges.push("Sleeper");
            if (title.includes("outdoor") || tags?.some(t => /outdoor/i.test(t))) badges.push("Outdoor");
            if (tags?.some(t => /new|arrival/i.test(t))) badges.push("New");
            const variantCount = node.variants?.edges?.length || 0;
            // Only when no swatches render below — otherwise the pill duplicates them.
            if (variantCount > 1 && colorOption && colorOption.values.length > 1 && swatches.length === 0) {
              badges.push(`${colorOption.values.length} colors`);
            }
            if (badges.length === 0) return null;
            return (
              <div className="flex flex-wrap justify-center gap-1 mt-1.5">
                {badges.slice(0, 2).map((b) => (
                  <span key={b} className="text-[9.5px] tracking-[0.14em] uppercase text-muted-foreground border border-border/70 px-1.5 py-0.5 rounded-full">
                    {b}
                  </span>
                ))}
              </div>
            );
          })()}

          {soldOut && (
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-1.5">
              Sold out — not available to order
            </p>
          )}

          {rating !== null && rating > 0 && (
            <div className="flex justify-center mt-1">
              <StarRating rating={rating} size={11} />
            </div>
          )}

          {price && (
            <div className="flex items-center justify-center gap-2 mt-1">
              {isOnSale && displayOriginalPrice && (
                <p className="text-[12px] text-muted-foreground line-through">
                  {formatPrice(displayOriginalPrice.toString(), price.currencyCode)}
                </p>
              )}
              <p className={`text-[13px] font-medium tabular-nums ${isOnSale ? "text-primary" : "text-foreground"}`}>
                {priceIsFrom && "From "}
                {formatPrice(price.amount, price.currencyCode)}
              </p>
            </div>
          )}
          {swatches.length > 0 && (
            <div className="flex items-center justify-center gap-1.5 mt-2">
              {swatches.map((s) => (
                <span
                  key={s.label}
                  title={s.label}
                  className="w-3 h-3 rounded-full ring-1 ring-border ring-offset-1 ring-offset-background flex-shrink-0 cursor-pointer hover:scale-125 transition-transform"
                  style={{ backgroundColor: s.color ?? undefined }}
                />
              ))}
              {colorOption && colorOption.values.length > 5 && (
                <span className="text-[10px] text-muted-foreground ml-0.5">
                  +{colorOption.values.length - 5}
                </span>
              )}
            </div>
          )}

          {canOffer && (
            requiresSelection ? (
              <Button asChild variant="outline" size="sm" className={`mt-3 h-9 w-full rounded-full text-[11px] uppercase tracking-wider ${CAN_HOVER ? "md:hidden" : ""}`}>
                <Link to={`/product/${node.handle}`}>View options</Link>
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleQuickAdd}
                disabled={addingToCart || isLoading}
                className={`mt-3 h-9 w-full rounded-full text-[11px] uppercase tracking-wider ${CAN_HOVER ? "md:hidden" : ""}`}
                aria-label={`Add ${displayTitle} to cart`}
              >
                {addingToCart ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShoppingCart className="h-3.5 w-3.5" />}
                {addingToCart ? "Adding" : "Quick Add"}
              </Button>
            )
          )}
        </div>
      </div>

      <QuickViewModal product={product} open={quickViewOpen} onClose={() => setQuickViewOpen(false)} />
    </>
  );
};
